import { getImageSources } from "@dotcom/dato";
import {
  type ParsedImage,
  convertFocalPointToCssObjectPosition,
} from "@dotcom/ui";
import {
  Alignments,
  AspectRatios,
  Breakpoints,
  Button,
  ButtonVariants,
  Colors,
  Icon,
  Icons,
  Image,
  Paragraph,
  Spacer,
  Spacings,
} from "@polestar/component-warehouse-react";
import { Link } from "@remix-run/react";

import {
  IconWrapper,
  IngressWrapper,
  StyledArticle,
  StyledAspectRatio,
  StyledContentGridColumn,
  StyledGrid,
  StyledHeadline,
  StyledImageGridColumn,
  StyledNewsArticle,
  StyledPicture,
  TextButtonWrapper,
} from "./NewsArticle.styles";

interface Images {
  imageMobile?: ParsedImage;
  imageTablet?: ParsedImage;
  imageDesktop?: ParsedImage;
}

interface NewsArticleProps {
  readonly slug?: string;
  readonly headline?: string;
  readonly ingress?: string;
  readonly publishDateLocalised?: string;
  readonly images?: Images;
  readonly isBig?: { desktop: boolean; mobile: boolean };
  readonly isPreviousArticleBig?: { desktop: boolean; mobile: boolean };
  readonly isHighlighted?: boolean;
  readonly highlightedLabel?: string;
  readonly readMoreLabel?: string;
  readonly locale: string;
}

const parseImageSources = (
  images?: Images,
  isBig?: {
    desktop: boolean;
    mobile: boolean;
  }
) => {
  return getImageSources({
    mobile: {
      url: images?.imageMobile
        ? images?.imageMobile.url
        : images?.imageDesktop?.url,
      imageWidth: isBig?.mobile ? 350 : 100,
      breakpoint: Breakpoints.mobile,
    },
    tablet: {
      url:
        images?.imageTablet && !isBig?.desktop
          ? images?.imageTablet.url
          : images?.imageDesktop?.url,
      imageWidth: isBig?.desktop ? 700 : 350,
      breakpoint: Breakpoints.tablet,
    },
    desktop: {
      url: images?.imageDesktop?.url,
      imageWidth: isBig?.desktop ? 706 : 350,
      breakpoint: Breakpoints.desktop,
    },
    desktopWide: {
      url: images?.imageDesktop?.url,
      imageWidth: isBig?.desktop ? 1056 : 512,
      breakpoint: Breakpoints.desktopWide,
    },
  });
};

const NewsArticle = ({
  slug,
  headline,
  ingress,
  isBig,
  isPreviousArticleBig,
  publishDateLocalised,
  isHighlighted,
  highlightedLabel,
  readMoreLabel,
  images,
  locale,
}: NewsArticleProps) => {
  const imageSources = parseImageSources(images, isBig);

  return (
    <StyledNewsArticle
      span={{
        mobile: 4,
        tablet: isBig?.desktop ? 8 : 4,
        desktop: isBig?.desktop ? 12 : 4,
      }}
      isBig={isBig}
      isPreviousArticleBig={isPreviousArticleBig}
    >
      <Link
        to={`/${locale}/news/${slug || ""}`}
        prefetch="intent"
        style={{ textDecoration: "none" }}
      >
        <StyledArticle isBig={isBig}>
          <StyledGrid isBig={isBig}>
            <StyledImageGridColumn
              span={{
                mobile: isBig?.mobile ? 4 : 1,
                tablet: 8,
                desktop: 12,
              }}
              isBig={isBig}
            >
              <StyledAspectRatio
                ratio={{
                  mobile: isBig?.mobile
                    ? AspectRatios.wide
                    : AspectRatios.square,
                  tablet: isBig?.desktop
                    ? AspectRatios.xWide
                    : AspectRatios.wide,
                  desktop: AspectRatios.xWide,
                }}
              >
                <StyledPicture stretch>
                  {imageSources.sources.map(({ media, srcSet }) => {
                    return <source key={media} srcSet={srcSet} media={media} />;
                  })}
                  <Image
                    stretch
                    src={imageSources.source}
                    alt={images?.imageDesktop?.alt}
                    objectFit="cover"
                    objectPosition={{
                      mobile: convertFocalPointToCssObjectPosition(
                        images?.imageMobile?.focalPoint
                      ),
                      tablet: convertFocalPointToCssObjectPosition(
                        images?.imageTablet?.focalPoint
                      ),
                      desktop: convertFocalPointToCssObjectPosition(
                        images?.imageDesktop?.focalPoint
                      ),
                      desktopWide: convertFocalPointToCssObjectPosition(
                        images?.imageDesktop?.focalPoint
                      ),
                    }}
                  />
                </StyledPicture>
              </StyledAspectRatio>
            </StyledImageGridColumn>
            <StyledContentGridColumn
              span={{
                mobile: isBig?.mobile ? 4 : 3,
                tablet: isBig?.desktop ? 7 : 8,
                desktop: isBig?.desktop ? 3 : 12,
              }}
              isBig={isBig}
            >
              <Spacer
                spacing={{
                  mobile: isBig?.mobile ? Spacings.medium : Spacings.none,
                  tablet: Spacings.medium,
                }}
              />
              <Paragraph as="time">
                {publishDateLocalised}
                {isHighlighted && <> | {highlightedLabel}</>}
              </Paragraph>
              <Spacer
                spacing={{
                  mobile: isBig?.mobile ? Spacings.medium : Spacings.xSmall,
                  tablet: Spacings.medium,
                }}
              />
              <StyledHeadline level={2} isBig={isBig}>
                {headline}
              </StyledHeadline>
              <IngressWrapper isBig={isBig}>
                <Spacer spacing={{ mobile: Spacings.medium }} />
                <Paragraph lineClamp={3}>{ingress}</Paragraph>
              </IngressWrapper>
              <TextButtonWrapper isBig={isBig}>
                <Spacer spacing={{ mobile: Spacings.medium }} />
                <Button
                  variant={ButtonVariants.text}
                  icon={Icons.arrowRight}
                  iconPosition={Alignments.right}
                >
                  {readMoreLabel}
                </Button>
              </TextButtonWrapper>
              <IconWrapper isBig={isBig}>
                <Icon icon={Icons.chevronRight} color={Colors.safetyOrange} />
              </IconWrapper>
            </StyledContentGridColumn>
          </StyledGrid>
        </StyledArticle>
      </Link>
    </StyledNewsArticle>
  );
};

export default NewsArticle;
